/**
 * Reusable header component for Topical Risk and answer flag drawer.
 * Displays a title, "Last Updated" date, risk status chip, and a divider.
 */
import DateDisplay from '@app/src/components/DateDisplay'
import SimpleRiskChip from '@app/src/pages/Assessments/AssessmentCompanyPage/SimpleRiskChip'
import type { RiskStatus } from '@app/src/types/resourceExplorer'
import { getRiskWeakerColor } from '@app/src/utils/riskStatusHelpers'
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

const SUPPLY_CHAIN_PRACTICES = 'Supply chain practices'

export interface DrawerHeaderProps {
  titleTranslationKey: string
  lastUpdated: string | Date
  riskStatus: RiskStatus
  topicName?: string
  hasInherentRisk?: boolean
  hasPerformance?: boolean
  providerName?: string
}

export const DrawerHeader: React.FC<DrawerHeaderProps> = ({
  titleTranslationKey,
  lastUpdated,
  riskStatus,
  topicName,
  hasInherentRisk,
  hasPerformance,
  providerName,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const bannerBgColor = getRiskWeakerColor(theme, riskStatus)
  const topicNameKey =
    topicName === SUPPLY_CHAIN_PRACTICES ? formatMessage({ id: 'topicalRisk.sectionHeader.supplyChain' }) : topicName
  const hasInherentRiskOrPerformance = hasInherentRisk || hasPerformance

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: bannerBgColor,
          display: 'flex',
          flexDirection: 'column',
          py: 1,
          pb: 1,
          borderRadius: '0px',
        }}
      >
        <Typography variant="subtitle1" sx={{ px: 3, pt: 2 }}>
          {formatMessage({ id: titleTranslationKey }, { title: topicNameKey })}
        </Typography>

        <Box px={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body1" color="text.secondary">
                {formatMessage({ id: 'general.lastUpdated' })}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                <DateDisplay value={lastUpdated} />
              </Typography>
            </Stack>
            <SimpleRiskChip riskStatus={riskStatus} larger />
          </Stack>
          <Box py={1} pb={2}>
            {topicName && providerName && hasInherentRiskOrPerformance && (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '16px' }}>
                {formatMessage(
                  {
                    id:
                      hasInherentRisk && hasPerformance
                        ? 'topicalRisk.riskContribution.both'
                        : hasInherentRisk
                          ? 'topicalRisk.riskContribution.inherentOnly'
                          : 'topicalRisk.riskContribution.performanceOnly',
                  },
                  { topicName: topicNameKey, providerName },
                )}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </>
  )
}

export default DrawerHeader
